import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    removeCard,
    updateCardPosition,
    updateCardSize,
    bringCardToFront,
    setBlinkingCard,
    addCardById as addCardByIdAction,
    addCard,
    updateCards,
    setActiveLayout
} from '../store/cardsSlice';
import { CARD_TYPE_CONFIGS } from '../config/cardConfig';
import Card from '../components/Card';
import Layouts from '../components/Layouts';
import UserDropdown from '../components/UserDropdown';
import LayoutNavBar from '../components/LayoutNavBar';
import { useState, useEffect } from 'react';
import { selectDataFeedProviders } from '../store/settingsSlice';
import { layoutPresets } from '../config/layoutPresets.tsx';

const Home = () => {
    const dispatch = useAppDispatch();
    const { cards, blinkingCardId } = useAppSelector((state) => state.cards);
    const connectedDataFeedProviders = useAppSelector(selectDataFeedProviders);
    const hasDataFeedConnected = connectedDataFeedProviders !== null;
    const [isAddCardDropdownOpen, setIsAddCardDropdownOpen] = useState(false);
    const [dragging, setDragging] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
    const [resizing, setResizing] = useState<{ id: string; edge: string; startX: number; startY: number; startWidth: number; startHeight: number; startPosX: number; startPosY: number } | null>(null);

    // Check if charts card exists and provider is connected
    useEffect(() => {
        const hasChartsCard = cards.some(card => card.content === 'charts');
        const hasConnectionWarning = cards.some(card => card.content === 'connection-warning');

        if (!hasChartsCard && !hasDataFeedConnected && !hasConnectionWarning) {
            // No charts card and not connected - add connection warning
            dispatch(addCardByIdAction('connection-warning'));
        } else if (hasConnectionWarning && (hasChartsCard || hasDataFeedConnected)) {
            // Has connection warning but charts exist or provider connected - remove it
            const warningCard = cards.find(card => card.content === 'connection-warning');
            if (warningCard) {
                dispatch(removeCard(warningCard.id));
            }
        }
    }, [cards, hasDataFeedConnected, dispatch]);

    // Load default layout preset when cards are empty
    useEffect(() => {
        if (cards.length === 0 && layoutPresets.length > 0) {
            const defaultLayout = layoutPresets[0]; // Use first preset (Professional Trader)
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight - 108; // Nav (60px) + Layout Nav Bar (48px)

            dispatch(setActiveLayout(null));

            setTimeout(() => {
                const layoutData = defaultLayout.cards.map((layoutCard, index) => {
                    const responsiveCard = {
                        ...layoutCard,
                        position: {
                            x: (layoutCard.position.x / 100) * viewportWidth,
                            y: (layoutCard.position.y / 100) * viewportHeight
                        },
                        size: {
                            width: (layoutCard.size.width / 100) * viewportWidth,
                            height: (layoutCard.size.height / 100) * viewportHeight
                        }
                    };

                    dispatch(addCard(responsiveCard));

                    return {
                        ...responsiveCard,
                        index
                    };
                });

                dispatch(updateCards(layoutData));
            }, 50);
        }
    }, [cards.length, dispatch]);

    const handleMouseDown = (e: React.MouseEvent, id: string) => {
        if (
            (e.target as HTMLElement).closest('.card-controls') ||
            (e.target as HTMLElement).closest('.resize-handle') ||
            (e.target as HTMLElement).closest('.dropdown-container') ||
            (e.target as HTMLElement).closest('button')
        ) {
            return;
        }

        const card = cards.find(c => c.id === id);
        if (!card) return;

        dispatch(bringCardToFront(id));
        setDragging({
            id,
            offsetX: e.clientX - card.position.x,
            offsetY: e.clientY - card.position.y
        });
    };

    const handleResizeMouseDown = (e: React.MouseEvent, id: string, edge: string) => {
        e.stopPropagation();
        const card = cards.find(c => c.id === id);
        if (!card) return;

        dispatch(bringCardToFront(id));
        setResizing({
            id,
            edge,
            startX: e.clientX,
            startY: e.clientY,
            startWidth: card.size.width,
            startHeight: card.size.height,
            startPosX: card.position.x,
            startPosY: card.position.y
        });
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (dragging) {
                const newX = e.clientX - dragging.offsetX;
                const newY = e.clientY - dragging.offsetY;

                // Get viewport constraints
                const mainPadding = 0; // Approximate padding
                const card = cards.find(c => c.id === dragging.id);

                if (card) {
                    // Constrain position within viewport
                    const constrainedX = Math.max(mainPadding, Math.min(newX, window.innerWidth - card.size.width - mainPadding));
                    const constrainedY = Math.max(mainPadding, Math.min(newY, window.innerHeight - card.size.height - 20));

                    dispatch(updateCardPosition({ id: dragging.id, position: { x: constrainedX, y: constrainedY } }));
                }
            }

            if (resizing) {
                const deltaX = e.clientX - resizing.startX;
                const deltaY = e.clientY - resizing.startY;

                const card = cards.find(c => c.id === resizing.id);
                if (card) {
                    let newWidth = resizing.startWidth;
                    let newHeight = resizing.startHeight;
                    let newX = resizing.startPosX;
                    let newY = resizing.startPosY;

                    const mainPadding = 48;
                    const navHeight = 60;

                    if (resizing.edge.includes('e')) {
                        newWidth = Math.max(300, Math.min(resizing.startWidth + deltaX, window.innerWidth - resizing.startPosX - mainPadding));
                    }
                    if (resizing.edge.includes('w')) {
                        const maxWidthIncrease = resizing.startPosX - mainPadding;
                        const deltaConstrained = Math.max(-maxWidthIncrease, Math.min(deltaX, resizing.startWidth - 300));
                        newWidth = resizing.startWidth - deltaConstrained;
                        newX = resizing.startPosX + deltaConstrained;
                    }
                    if (resizing.edge.includes('s')) {
                        newHeight = Math.max(200, Math.min(resizing.startHeight + deltaY, window.innerHeight - resizing.startPosY - 20));
                    }
                    if (resizing.edge.includes('n')) {
                        const maxHeightIncrease = resizing.startPosY - navHeight - 20;
                        const deltaConstrained = Math.max(-maxHeightIncrease, Math.min(deltaY, resizing.startHeight - 200));
                        newHeight = resizing.startHeight - deltaConstrained;
                        newY = resizing.startPosY + deltaConstrained;
                    }

                    dispatch(updateCardSize({
                        id: resizing.id,
                        size: { width: newWidth, height: newHeight },
                        position: { x: newX, y: newY }
                    }));
                }
            }
        };

        const handleMouseUp = () => {
            setDragging(null);
            setResizing(null);
        };

        if (dragging || resizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }

        return undefined;
    }, [dragging, resizing, cards, dispatch]);

    const addCardById = (cardType: string) => {
        dispatch(addCardByIdAction(cardType));

        const cardConfig = CARD_TYPE_CONFIGS.find(config => config.type === cardType);
        if (!cardConfig) return;

        const existingCard = cards.find(card => card.title === cardConfig.title);

        if (existingCard && blinkingCardId === existingCard.id) {
            setTimeout(() => dispatch(setBlinkingCard(null)), 3000);
        }

        setIsAddCardDropdownOpen(false);
    };

    return (
        <>
            <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
                {/* Navigation */}
                <nav className="shadow-sm" style={{ backgroundColor: 'var(--color-surface)', borderBottom: '1px solid var(--color-border-light)' }}>
                    <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24" style={{ padding: '0 var(--spacing-md)' }}>
                        <div className="flex justify-between items-center" style={{ height: '60px' }}>
                            <h3 className="font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)' }}>
                                Ocktra
                            </h3>
                            <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                                {/* Add Card Button */}
                                <div style={{ position: 'relative' }}>
                                    <button
                                        onClick={() => setIsAddCardDropdownOpen(!isAddCardDropdownOpen)}
                                        className="flex items-center rounded-lg font-medium transition-all duration-300"
                                        style={{
                                            backgroundColor: 'var(--color-primary-600)',
                                            color: 'white',
                                            padding: '6px 12px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '0.8125rem',
                                            gap: '6px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                                        }}
                                    >
                                        <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span className="hidden sm:inline">Add Card</span>
                                    </button>

                                    {/* Add Card Dropdown */}
                                    {isAddCardDropdownOpen && (
                                        <>
                                            <div
                                                style={{
                                                    position: 'fixed',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    zIndex: 10
                                                }}
                                                onClick={() => setIsAddCardDropdownOpen(false)}
                                            />
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 'calc(100% + var(--spacing-sm))',
                                                    right: 0,
                                                    minWidth: '220px',
                                                    backgroundColor: 'var(--color-surface)',
                                                    border: '1px solid var(--color-border-light)',
                                                    borderRadius: 'var(--radius-lg)',
                                                    boxShadow: 'var(--shadow-xl)',
                                                    padding: 'var(--spacing-sm)',
                                                    zIndex: 10000
                                                }}
                                            >
                                                {CARD_TYPE_CONFIGS.map(cardConfig => (
                                                    <button
                                                        key={cardConfig.type}
                                                        onClick={() => addCardById(cardConfig.type)}
                                                        style={{
                                                            width: '100%',
                                                            textAlign: 'left',
                                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                                            borderRadius: 'var(--radius-md)',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: 'var(--color-text-primary)',
                                                            fontSize: '0.875rem',
                                                            transition: 'background-color 200ms ease-in-out',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--spacing-sm)'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={cardConfig.icon} />
                                                        </svg>
                                                        {cardConfig.title}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Layouts Dropdown */}
                                <Layouts />

                                {/* User Dropdown */}
                                <UserDropdown />
                            </div>
                        </div>
                    </div>
                </nav>

                <LayoutNavBar />

                <main
                    className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24"
                    style={{
                        position: 'relative',
                        minHeight: 'calc(100vh - 114px)',
                        // paddingBottom: '48px',
                        backgroundImage: `radial-gradient(circle, var(--color-gray-300) 1px, transparent 1px)`,
                        backgroundSize: '24px 24px',
                        backgroundPosition: '0 0',
                        boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.05)'
                    }}
                >
                    {/* Cards Container */}
                    {cards.map(card => (
                        <Card
                            key={card.id}
                            card={card}
                            blinkingCardId={blinkingCardId}
                            onMouseDown={handleMouseDown}
                            onResizeMouseDown={handleResizeMouseDown}
                        />
                    ))}

                </main>
            </div>
        </>
    );
};

export default Home;
